/**
 * Prepayment Match Dialog Component
 * 
 * P2 - Prepayment Renewal Detection
 * 
 * Allows users to match a bank feed transaction to an existing prepayment
 * to extend it as a renewal (e.g., annual rent renewal) or create a new one.
 * 
 * Features:
 * - Detects potential renewal based on vendor name, amount, and type
 * - Shows existing prepayment details and remaining balance
 * - Allows extending the schedule with new payment
 * - Option to create new prepayment if not a renewal
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import {
  Loader2,
  Calendar,
  CalendarPlus,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ArrowUpRight,
  Sparkles,
  RefreshCw,
  Building2,
  Clock,
  DollarSign,
  PlusCircle
} from 'lucide-react';

// Prepayment type labels
const PREPAYMENT_TYPE_LABELS = {
  rent: 'Rent',
  insurance: 'Insurance',
  subscription: 'Subscription',
  maintenance: 'Maintenance',
  license: 'Software License',
  advertising: 'Advertising',
  professional_fees: 'Professional Fees',
  other: 'Other'
};

export const PrepaymentMatchDialog = ({
  open,
  onOpenChange,
  bankTransaction,
  formatAmount,
  onMatchComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [selectedPrepaymentId, setSelectedPrepaymentId] = useState(null);
  const [extensionMonths, setExtensionMonths] = useState(12);

  // Fetch potential matches when dialog opens
  useEffect(() => {
    if (open && bankTransaction?.id) {
      fetchPotentialMatches();
    } else {
      setMatchData(null);
      setSelectedPrepaymentId(null);
      setExtensionMonths(12);
    }
  }, [open, bankTransaction?.id]);

  const fetchPotentialMatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/bank-feeds/prepayment-matches/${bankTransaction.id}`);
      setMatchData(response.data);
      
      // Auto-select best match if confidence is high
      if (response.data?.best_match?.confidence_score >= 60) {
        setSelectedPrepaymentId(response.data.best_match.prepayment_id);
        // Default extension based on original coverage
        if (response.data.best_match.coverage_months) {
          setExtensionMonths(response.data.best_match.coverage_months);
        }
      }
    } catch (error) {
      console.error('Error fetching prepayment matches:', error);
      toast.error('Failed to load potential prepayment matches');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendPrepayment = async () => {
    if (!selectedPrepaymentId) {
      toast.error('Please select a prepayment to extend');
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('prepayment_id', selectedPrepaymentId);
      formData.append('extension_months', extensionMonths.toString());

      await axios.post(
        `${API}/bank-feeds/review/${bankTransaction.id}/prepayment-extend`,
        formData);

      toast.success(`Prepayment extended by ${extensionMonths} months`);
      onMatchComplete?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error extending prepayment:', error);
      toast.error(error.response?.data?.detail || 'Failed to extend prepayment');
    } finally {
      setProcessing(false);
    }
  };

  const handleNotARenewal = async () => {
    // Close dialog and trigger new prepayment creation flow
    toast.info('Please create a new prepayment for this transaction');
    onOpenChange(false);
    // This will fall back to the normal prepayment creation flow
  };

  const getTypeBadge = (type) => {
    const colors = {
      rent: 'bg-blue-100 text-blue-700',
      insurance: 'bg-green-100 text-green-700',
      subscription: 'bg-purple-100 text-purple-700',
      maintenance: 'bg-orange-100 text-orange-700',
      license: 'bg-cyan-100 text-cyan-700',
      advertising: 'bg-pink-100 text-pink-700'
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const transactionAmount = Math.abs(bankTransaction?.amount || 0);
  const selectedPrepayment = matchData?.matching_prepayments?.find(p => p.prepayment_id === selectedPrepaymentId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-green-600" />
            Prepayment Renewal Detection
          </DialogTitle>
          <DialogDescription>
            This transaction may be a renewal of an existing prepayment. Select one to extend or create a new prepayment.
          </DialogDescription>
        </DialogHeader>

        {/* Bank Transaction Summary */}
        {bankTransaction && (
          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-red-100">
                  <ArrowUpRight className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="font-medium">{bankTransaction.description?.slice(0, 60)}...</p>
                  <p className="text-sm text-muted-foreground">{bankTransaction.date}</p>
                </div>
              </div>
              <span className="text-lg font-semibold text-red-600">
                -{formatAmount?.(transactionAmount) || transactionAmount?.toLocaleString()}
              </span>
            </div>
          </div>
        )}

        <Separator />

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-600" />
            <span className="ml-3 text-muted-foreground">Finding matching prepayments...</span>
          </div>
        )}

        {/* Match Results */}
        {!loading && matchData && (
          <div className="flex-1 overflow-hidden">
            {/* Best Match Suggestion */}
            {matchData.best_match && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium">Likely Renewal</span>
                  <Badge variant="outline" className={
                    matchData.best_match.confidence_score >= 70 ? 'bg-green-50 text-green-700 border-green-200' :
                    matchData.best_match.confidence_score >= 50 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-gray-50 text-gray-700 border-gray-200'
                  }>
                    {matchData.best_match.confidence_score}% confidence
                  </Badge>
                </div>
                <Card 
                  className={`cursor-pointer transition-all ${
                    selectedPrepaymentId === matchData.best_match.prepayment_id 
                      ? 'ring-2 ring-green-500 bg-green-50/50' 
                      : 'hover:border-green-300'
                  }`}
                  onClick={() => {
                    setSelectedPrepaymentId(matchData.best_match.prepayment_id);
                    if (matchData.best_match.coverage_months) {
                      setExtensionMonths(matchData.best_match.coverage_months);
                    }
                  }}
                  data-testid="suggested-prepayment-match-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-green-600" />
                          <span className="font-medium">{matchData.best_match.vendor_name || 'Unknown Vendor'}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={getTypeBadge(matchData.best_match.prepayment_type)}>
                            {PREPAYMENT_TYPE_LABELS[matchData.best_match.prepayment_type] || matchData.best_match.prepayment_type}
                          </Badge>
                          {matchData.best_match.coverage_months && (
                            <Badge variant="outline" className="text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              {matchData.best_match.coverage_months} months
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Original: {formatAmount?.(matchData.best_match.original_amount) || matchData.best_match.original_amount?.toLocaleString()}</span>
                          <span>Remaining: {formatAmount?.(matchData.best_match.remaining_balance) || matchData.best_match.remaining_balance?.toLocaleString()}</span>
                        </div>
                        {/* Match Reasons */}
                        {matchData.best_match.match_reasons?.length > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <span className="font-medium">Why: </span>
                            {matchData.best_match.match_reasons.slice(0, 2).join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        {matchData.best_match.end_date && (
                          <div className="text-xs text-muted-foreground">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            Ends: {matchData.best_match.end_date}
                          </div>
                        )}
                        {selectedPrepaymentId === matchData.best_match.prepayment_id && (
                          <CheckCircle2 className="w-5 h-5 text-green-600 mt-2 ml-auto" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Other Prepayments */}
            {matchData.matching_prepayments && matchData.matching_prepayments.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {matchData.best_match ? 'Other Active Prepayments' : 'Active Prepayments'}
                </Label>
                <ScrollArea className="h-[180px] pr-4">
                  <RadioGroup 
                    value={selectedPrepaymentId || ''} 
                    onValueChange={(val) => {
                      setSelectedPrepaymentId(val);
                      const prep = matchData.matching_prepayments.find(p => p.prepayment_id === val);
                      if (prep?.coverage_months) {
                        setExtensionMonths(prep.coverage_months);
                      }
                    }}
                    className="space-y-2"
                  >
                    {matchData.matching_prepayments
                      .filter(prep => prep.prepayment_id !== matchData.best_match?.prepayment_id)
                      .map((prep) => (
                        <div
                          key={prep.prepayment_id}
                          className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedPrepaymentId === prep.prepayment_id 
                              ? 'ring-2 ring-green-500 bg-green-50/50' 
                              : 'hover:bg-muted/50'
                          }`}
                          onClick={() => {
                            setSelectedPrepaymentId(prep.prepayment_id);
                            if (prep.coverage_months) {
                              setExtensionMonths(prep.coverage_months);
                            }
                          }}
                          data-testid={`prepayment-option-${prep.prepayment_id}`}
                        >
                          <RadioGroupItem value={prep.prepayment_id} id={prep.prepayment_id} />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{prep.vendor_name || 'Unknown'}</span>
                              <Badge variant="outline" className="text-xs">
                                {PREPAYMENT_TYPE_LABELS[prep.prepayment_type] || prep.prepayment_type}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              <span>Original: {formatAmount?.(prep.original_amount) || prep.original_amount?.toLocaleString()}</span>
                              {prep.end_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Ends: {prep.end_date}
                                </span>
                              )}
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {prep.confidence_score}%
                          </Badge>
                        </div>
                      ))}
                  </RadioGroup>
                </ScrollArea>
              </div>
            )}

            {/* Extension Months Input */}
            {selectedPrepaymentId && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CalendarPlus className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium">Extension Period</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={extensionMonths}
                      onChange={(e) => setExtensionMonths(parseInt(e.target.value) || 12)}
                      min={1}
                      max={60}
                      className="w-20 h-8 text-center"
                    />
                    <span className="text-sm text-muted-foreground">months</span>
                  </div>
                </div>
                {selectedPrepayment && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    New total coverage: {(selectedPrepayment.coverage_months || 0) + extensionMonths} months
                  </div>
                )}
              </div>
            )}

            {/* No Prepayments Found */}
            {(!matchData.matching_prepayments || matchData.matching_prepayments.length === 0) && !matchData.best_match && (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-medium mb-1">No Matching Prepayments Found</h3>
                <p className="text-sm text-muted-foreground">
                  No active prepayments match this transaction.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  You can create a new prepayment for this transaction.
                </p>
              </div>
            )}
          </div>
        )}

        <Separator />

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={handleNotARenewal}
            disabled={processing}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            data-testid="create-new-prepayment-btn"
          >
            <PlusCircle className="w-4 h-4 mr-2" />
            Create New Prepayment
          </Button>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExtendPrepayment}
              disabled={processing || !selectedPrepaymentId}
              className="bg-green-600 hover:bg-green-700"
              data-testid="extend-prepayment-btn"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Extend Prepayment
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PrepaymentMatchDialog;
